import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, ArrowLeft, Loader2, CheckCircle, XCircle, Clock } from 'lucide-react';
import { toast } from 'sonner';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusConfig = {
  pending: { icon: Clock, color: 'bg-yellow-400', label: 'Pending' },
  confirmed: { icon: CheckCircle, color: 'bg-green-500', label: 'Confirmed' },
  cancelled: { icon: XCircle, color: 'bg-red-500', label: 'Cancelled' }
};

export const AdminBookings = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);

  const fetchBookings = useCallback(async () => {
    try {
      const response = await fetch(`${API}/bookings`, { credentials: 'include' });
      if (!response.ok) throw new Error('Failed to fetch');
      const data = await response.json();
      setBookings(data);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);

  const updateStatus = async (bookingId, newStatus) => {
    setUpdating(bookingId);
    try {
      const response = await fetch(`${API}/admin/bookings/${bookingId}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) throw new Error('Failed to update status');

      toast.success('Status updated!');
      fetchBookings();
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Failed to update status');
    } finally {
      setUpdating(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-bookings">
      {/* Header */}
      <header className="bg-black border-b-4 border-[#00FFFF] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/admin" className="flex items-center gap-2 text-white hover:text-[#00FFFF] transition-colors">
              <ArrowLeft size={20} />
              <span className="font-space-grotesk font-semibold">Back to Dashboard</span>
            </Link>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-8">
          <Calendar className="w-8 h-8 text-[#00FFFF]" />
          <h1 className="font-permanent-marker text-3xl text-black">Event Bookings</h1>
        </div>

        {/* Bookings List */}
        {loading ? (
          <div className="text-center py-20">
            <Loader2 className="w-12 h-12 text-[#00FFFF] animate-spin mx-auto" />
          </div>
        ) : bookings.length === 0 ? (
          <div className="text-center py-20">
            <div className="card-graffiti inline-block">
              <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="font-space-grotesk text-xl text-gray-600">No booking requests yet.</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {bookings.map((booking, index) => {
              const StatusIcon = statusConfig[booking.status]?.icon || Clock;
              return (
                <motion.div
                  key={booking.booking_id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="card-graffiti"
                  data-testid={`booking-item-${index}`}
                >
                  <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`inline-flex items-center gap-1 px-3 py-1 ${statusConfig[booking.status]?.color || 'bg-gray-400'} text-black text-sm font-bangers rounded-full border-2 border-black`}>
                          <StatusIcon size={14} />
                          {statusConfig[booking.status]?.label || booking.status}
                        </span>
                        <span className="font-space-grotesk text-sm text-gray-500">
                          {booking.event_type}
                        </span>
                      </div>
                      
                      <h3 className="font-bangers text-xl text-black">{booking.name}</h3>
                      
                      <div className="grid sm:grid-cols-2 gap-2 mt-2 font-space-grotesk text-sm text-gray-600">
                        <p><strong>Email:</strong> {booking.email}</p>
                        <p><strong>Phone:</strong> {booking.phone}</p>
                        <p><strong>Date:</strong> {booking.event_date}</p>
                        <p><strong>Guests:</strong> {booking.guest_count}</p>
                      </div>
                      
                      {booking.message && (
                        <div className="mt-3 p-3 bg-gray-100 rounded-lg border-2 border-gray-300">
                          <p className="font-space-grotesk text-sm text-gray-700">{booking.message}</p>
                        </div>
                      )}
                    </div>

                    <div className="lg:w-40">
                      <label className="block font-space-grotesk font-semibold text-sm mb-1">Update Status</label>
                      <Select
                        value={booking.status}
                        onValueChange={(value) => updateStatus(booking.booking_id, value)}
                        disabled={updating === booking.booking_id}
                      >
                        <SelectTrigger className="w-full border-2 border-black rounded-lg" data-testid={`status-select-${index}`}>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="confirmed">Confirmed</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminBookings;
